import React from 'react';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  Button,
  makeStyles,
  Text,
  Badge,
  tokens,
} from '@fluentui/react-components';
import {
  DismissRegular,
  CheckmarkCircleRegular,
  ErrorCircleRegular,
  ClockRegular,
} from '@fluentui/react-icons';
import { SiteDetailsModalProps, ClockCycleStatus, CYCLE_STATUS_LABELS } from '../types';

const useStyles = makeStyles({
  dialogSurface: {
    maxWidth: '1000px',
    width: '95vw',
    maxHeight: '85vh',
    display: 'flex',
    flexDirection: 'column',
    margin: 'auto',
    position: 'fixed',
    top: '5vh',
    left: '50%',
    transform: 'translateX(-50%)',
  },
  dialogBody: {
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    maxHeight: 'calc(90vh - 120px)',
    width: '100%',
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginBottom: '20px',
    width: '100%',
  },
  siteTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },
  siteInfo: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '8px',
    flexWrap: 'nowrap',
    alignItems: 'center',
    width: '100%',
    fontSize: '14px',
    color: tokens.colorNeutralForeground3,
  },
  summarySection: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
    gap: '8px',
    padding: '16px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
    marginBottom: '20px',
  },
  summaryItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    alignItems: 'center',
    textAlign: 'center',
    flex: 1,
  },
  summaryLabel: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  summaryValue: {
    fontSize: '18px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },
  guardsSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    width: '100%',
  },
  guardCard: {
    padding: '16px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
    width: '100%',
  },
  guardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
    flexWrap: 'nowrap',
    gap: '8px',
  },
  guardName: {
    fontSize: '16px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },
  guardCode: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  complianceBadge: {
    fontSize: '14px',
    fontWeight: '600',
  },
  cyclesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '12px',
    width: '100%',
  },
  cycleItem: {
    padding: '12px',
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  cycleHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  cycleNumber: {
    fontSize: '12px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground2,
  },
  cycleTime: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  cycleStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    fontWeight: '500',
  },
  currentCycle: {
    backgroundColor: tokens.colorNeutralBackground2,
  },
});

const getCycleStatusIcon = (status: ClockCycleStatus) => {
  switch (status) {
    case ClockCycleStatus.ON_TIME:
      return (
        <CheckmarkCircleRegular
          fontSize={16}
          style={{ color: tokens.colorPaletteGreenForeground1 }}
        />
      );
    case ClockCycleStatus.LATE:
      return (
        <ErrorCircleRegular
          fontSize={16}
          style={{ color: tokens.colorPaletteDarkOrangeForeground1 }}
        />
      );
    case ClockCycleStatus.MISSED:
      return (
        <ErrorCircleRegular fontSize={16} style={{ color: tokens.colorPaletteRedForeground1 }} />
      );
    case ClockCycleStatus.PENDING:
      return <ClockRegular fontSize={16} style={{ color: tokens.colorNeutralForeground3 }} />;
  }
};

const getCycleStatusColor = (status: ClockCycleStatus): string => {
  switch (status) {
    case ClockCycleStatus.ON_TIME:
      return tokens.colorPaletteGreenForeground1;
    case ClockCycleStatus.LATE:
      return tokens.colorPaletteDarkOrangeForeground1;
    case ClockCycleStatus.MISSED:
      return tokens.colorPaletteRedForeground1;
    case ClockCycleStatus.PENDING:
      return tokens.colorNeutralForeground3;
  }
};

const getComplianceBadgeColor = (compliance: number): 'success' | 'warning' | 'danger' => {
  if (compliance === 100) {
    return 'success';
  }
  if (compliance >= 50) {
    return 'warning';
  }
  return 'danger';
};

export const SiteDetailsModal: React.FC<SiteDetailsModalProps> = ({ site, open, onClose }) => {
  const styles = useStyles();

  if (!site) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface className={styles.dialogSurface} aria-describedby={undefined}>
        <DialogTitle
          action={<Button appearance="subtle" icon={<DismissRegular />} onClick={onClose} />}
        >
          Site Clock-In Details
        </DialogTitle>

        <DialogBody className={styles.dialogBody}>
          <div className={styles.header}>
            <Text className={styles.siteTitle}>{site.site_name}</Text>
            <div className={styles.siteInfo}>
              <span>Code: {site.site_code}</span>
              <span>•</span>
              <span>Client: {site.client_name}</span>
              <span>•</span>
              <span>Shift: {site.shift_name}</span>
              <span>•</span>
              <span>Date: {site.shift_date}</span>
            </div>
          </div>

          <div className={styles.summarySection}>
            <div className={styles.summaryItem}>
              <Text className={styles.summaryLabel}>Compliance</Text>
              <Text className={styles.summaryValue}>{site.compliance_percentage.toFixed(0)}%</Text>
            </div>
            <div className={styles.summaryItem}>
              <Text className={styles.summaryLabel}>Assigned Guards</Text>
              <Text className={styles.summaryValue}>{site.assigned_guards_count}</Text>
            </div>
            <div className={styles.summaryItem}>
              <Text className={styles.summaryLabel}>Clocked In</Text>
              <Text className={styles.summaryValue}>{site.clocked_in_guards_count}</Text>
            </div>
            <div className={styles.summaryItem}>
              <Text className={styles.summaryLabel}>Required Cycles</Text>
              <Text className={styles.summaryValue}>{site.required_clock_cycles}</Text>
            </div>
            <div className={styles.summaryItem}>
              <Text className={styles.summaryLabel}>Completed Cycles</Text>
              <Text className={styles.summaryValue}>{site.completed_cycles}</Text>
            </div>
          </div>

          <div className={styles.guardsSection}>
            {site.guards.map(guard => (
              <div key={guard.employee_id} className={styles.guardCard}>
                <div className={styles.guardHeader}>
                  <div>
                    <Text className={styles.guardName}>{guard.employee_name}</Text>
                    <Text className={styles.guardCode}>{guard.employee_code}</Text>
                  </div>
                  <Badge
                    appearance="filled"
                    color={getComplianceBadgeColor(guard.overall_compliance)}
                    className={styles.complianceBadge}
                  >
                    {guard.overall_compliance.toFixed(0)}% Compliance
                  </Badge>
                </div>

                <div className={styles.cyclesGrid}>
                  {guard.cycles.map(cycle => (
                    <div
                      key={cycle.cycle_number}
                      className={`${styles.cycleItem} ${cycle.is_current ? styles.currentCycle : ''}`}
                      style={
                        cycle.is_current
                          ? { borderColor: tokens.colorBrandStroke1, borderWidth: '2px' }
                          : undefined
                      }
                    >
                      <div className={styles.cycleHeader}>
                        <Text className={styles.cycleNumber}>
                          Cycle {cycle.cycle_number}
                          {cycle.is_current && ' (Current)'}
                        </Text>
                      </div>
                      <Text className={styles.cycleTime}>Expected: {cycle.expected_time}</Text>
                      <Text className={styles.cycleTime}>
                        Window: {cycle.window_start} - {cycle.window_end}
                      </Text>
                      {cycle.clock_in_time && (
                        <Text className={styles.cycleTime}>Clocked: {cycle.clock_in_time}</Text>
                      )}
                      <div
                        className={styles.cycleStatus}
                        style={{ color: getCycleStatusColor(cycle.status) }}
                      >
                        {getCycleStatusIcon(cycle.status)}
                        <span>{CYCLE_STATUS_LABELS[cycle.status]}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </DialogBody>

        <DialogActions>
          <Button appearance="primary" onClick={onClose}>
            Close
          </Button>
        </DialogActions>
      </DialogSurface>
    </Dialog>
  );
};
